import {inject, Injectable} from '@angular/core';
import {LocalAnalysis} from "../../core/models/local-analysis";
import {
  catchError,
  filter,
  firstValueFrom,
  forkJoin,
  from,
  map,
  Observable,
  of,
  switchMap,
  tap,
  throwError
} from "rxjs";
import {Analysis, AnalysisPostcode} from "../../core/models/analysis";
import {CustomError} from "../../core/models/error";
import {PictureStatus} from "../../core/models/picture";
import {TyreAnalysisRequestDto} from "../../core/dto/tyre-analysis-request-dto";
import {StepType} from "../../core/models/step";
import * as Sentry from "@sentry/angular";
import {Utils} from "../utils/generics.utils";
import {TyreAnalysisResponseDto} from "../../core/dto/tyre-analysis-response-dto";
import {AnalysisStatusEnum} from "../../core/models/analysis-status";
import {AnalysisResponseDto} from "../../core/dto/analysis-response-dto";
import {ReferenceApiService} from "../../core/api/reference-api/reference-api.service";
import {PictureUploadService} from "./picture-upload.service";
import {ApiService} from "../../core/api/api.service";
import {IndexedDBService} from "./indexed-db.service";
import {PostcodeService} from "../../features/shared/postcode/postcode.service";
import {TyrePicture} from "../../core/models/tyrePicture";
import {Product} from "../../core/models/product";
import {I18nService} from "./i18n.service";
import {AppStateService} from "../../core/state/app-state.service";
import {PhotoshootService} from "../../features/shared/photoshoot/services/photoshoot.service";

@Injectable({
  providedIn: 'root'
})
export class AnalysisService {

  protected referenceApi = inject(ReferenceApiService);
  protected postcodeService = inject(PostcodeService);
  protected uploadService = inject(PictureUploadService);
  protected api = inject(ApiService);
  protected indexedDB = inject(IndexedDBService);
  protected languageService = inject(I18nService);
  protected appStateService = inject(AppStateService);
  protected photoshootService = inject(PhotoshootService);

  createAnalysis(localAnalysis?: LocalAnalysis): Observable<Analysis> {
    const {
      vehicle = this.appStateService.vehicle(),
      product = this.appStateService.product(),
      company = this.appStateService.company(),
      fleet = this.appStateService.fleet(),
      postcode = this.appStateService.postcode(),
      externalId = this.appStateService.externalId()
    } = localAnalysis || {};

    if (vehicle?.id && company?.id) {
      return this.referenceApi
        .createAnalysis(
          vehicle.id,
          product,
          company.id,
          fleet,
          this.languageService.getLanguage(),
          vehicle.mileage,
          externalId
        )
        .pipe(
          switchMap((analysis) => this.handleAnalysisCreation(analysis, postcode)),
          switchMap((analysis) => this.handleLocalAnalysis(analysis, localAnalysis))
        );
    } else {
      return throwError(() => new Error(CustomError.NO_SUCH_VEHICLE));
    }
  }

  createTyreAnalysis(picture: TyrePicture) {
    const analysisId = this.appStateService.analysis()?.id;
    const dto = new TyreAnalysisRequestDto('jpg', picture.step.position, picture.step.type);
    const vehicleId = this.appStateService.vehicle()?.id;

    if (vehicleId) {
      if (analysisId) {
        return this.referenceApi.getAnalysis(vehicleId, this.appStateService.product()).pipe(switchMap((analysis) => {
          const steps = picture.step.type === StepType.REFERENCE ? analysis.referenceTyres : analysis.wearTyres;
          const tyreAnalysis = steps.find((ta) => ta.position === picture.step.position);

          if (tyreAnalysis) {
            Sentry.captureMessage('Tried to create an existing tyre analysis', {extra: {picture: Utils.sanitizePictures(picture), tyreAnalysis}});
            return of({
              ...tyreAnalysis,
              id: tyreAnalysis.tyreAnalysisId
            } as TyreAnalysisResponseDto);
          } else {
            return this.referenceApi.createTyreAnalysis(analysisId, dto);
          }
        }))
      } else {
        return this.createAnalysis().pipe(switchMap((analysis) => this.referenceApi.createTyreAnalysis(analysis.id!, dto)))
      }
    } else {
      return throwError(() => new Error(CustomError.NO_SUCH_VEHICLE));
    }
  }

  abortAnalysis() {
    const analysisId = this.appStateService.analysis()?.id;

    if (!analysisId) {
      return throwError(() => new Error(CustomError.NO_ANALYSIS));
    }

    return this.referenceApi.updateAnalysis(analysisId, AnalysisStatusEnum.ABORTED).pipe(
      tap(() => this.uploadService.cancelUpload()),
      tap(() => this.appStateService.clearLocalPicturesAndAnalysis()),
      switchMap(() => {
        if (this.appStateService.product() === Product.COLLECT) {
          return from(this.appStateService.clear()).pipe(map(() => null));
        } else {
          return this.createAnalysis();
        }
      })
    );
  }


  getAnalysis() {
    const vehicleId = this.appStateService.vehicle()?.id;
    if (!vehicleId) {
      return throwError(() => new Error(CustomError.NO_SUCH_VEHICLE));
    }

    return this.referenceApi.getAnalysis(vehicleId, this.appStateService.product()!).pipe(
      switchMap((response) => {
        if (response.status === AnalysisStatusEnum.UPLOADED) {
          return throwError(() => new Error(CustomError.ONGOING_ANALYSIS));
        } else {
          const analysis = new Analysis(response);
          this.appStateService.patch({analysis});
          const pictures = [
            ...analysis.referenceTyres.map(rt => TyrePicture.fromAnalysis(rt, StepType.REFERENCE)),
            ...analysis.wearTyres.map(wt => TyrePicture.fromAnalysis(wt, StepType.WEAR))
          ];
          this.photoshootService.setPictures(pictures);
          return of(analysis);
        }
      })
    );
  }

  private handleAnalysisCreation(analysisResponseDto: AnalysisResponseDto, postcode?: AnalysisPostcode): Observable<Analysis> {
    const analysis = new Analysis(analysisResponseDto);
    this.photoshootService.clearPictures();
    this.appStateService.patch({analysis});
    return this.createContact(analysis).pipe(
      switchMap((analysis) => this.updatePostcode(analysis, postcode))
    );
  }

  private createContact(analysis: Analysis) {
    const phone = this.appStateService.contact()?.phone;
    const email = this.appStateService.contact()?.email;
    if ((phone || email) && analysis?.id) {
      return this.referenceApi.createContact(analysis.id, email, phone).pipe(
        map(() => analysis),
        catchError((err) => {
          if (err.status === 400 && err.error.error === 'RESOURCE_ALREADY_EXIST') {
            return of(analysis);
          }
          Sentry.captureException(() => new Error('Unable to create contact'), {extra: {err}})
          return throwError(() => err);
        }));
    }
    return of(analysis);
  }

  private updatePostcode(analysis: Analysis, postcode?: AnalysisPostcode)  {
    return postcode
      ? this.postcodeService.setPostcode(analysis.id!, postcode).pipe(
        map((response) => {
          analysis.postcode = {
            codeCommune: response.body?.inseeCodeTravelZone,
            codePostal: response.body?.zipCodeTravelZone
          };
          this.appStateService.patch({analysis, postcode: analysis.postcode});
          return analysis;
        })
      )
      : of(analysis);
  }

  private handleLocalAnalysis(analysis: Analysis, localAnalysis?: LocalAnalysis) {
    if (localAnalysis) {
      return from(this.createSpareTyre(analysis, localAnalysis)).pipe(
        switchMap(() => this.indexedDB.getAllTyresPictures()),
        switchMap((pictures) => {
          return this.getPicturesAndCreateTyreAnalyses(pictures, analysis)
        }),
        switchMap((value) => this.uploadPictures(value))
      );
    }
    return of(analysis);
  }

  private createSpareTyre(analysis: Analysis, localAnalysis: LocalAnalysis) {
    if (analysis.id && localAnalysis.spareTyreResult?.spareTyreResult) {
      return this.referenceApi.createSpareTyreAnalysis$(analysis.id).pipe(
        map((response) => response.id),
        filter((vehicleAnalysisId) => !!vehicleAnalysisId),
        switchMap((vehicleAnalysisId) => this.referenceApi.updateSpareTyreAnalysis$(vehicleAnalysisId, localAnalysis.spareTyreResult!.spareTyreResult!).pipe(
          map(() => vehicleAnalysisId))
        ),
        switchMap((vehicleAnalysisId) => this.referenceApi.updateVehicleAnalysis$(vehicleAnalysisId, AnalysisStatusEnum.UPLOADED).pipe(
          map(() => vehicleAnalysisId))
        ),
        tap((vehicleAnalysisId) =>
          // @ts-ignore
          this.appStateService.patch( {analysis: {
            ...this.appStateService.analysis(),
            spareTyreResult: {
              vehicleAnalysisId,
              spareTyreResult: localAnalysis.spareTyreResult!.spareTyreResult!,
              status: AnalysisStatusEnum.UPLOADED
            }
          }}))
      );
    } else {
      return of(undefined);
    }
  }

  private getPicturesAndCreateTyreAnalyses(pictures: TyrePicture[], analysis: Analysis) {
    pictures = pictures.map((p) => {
      p.status = PictureStatus.UPLOADING;
      return p;
    });
    this.indexedDB.changePicturesStatus(PictureStatus.UPLOADING);
    const promises: Promise<TyreAnalysisResponseDto>[] = [];
    for (const picture of pictures) {
      promises.push(firstValueFrom(this.createTyreAnalysis(picture)));
    }
    return forkJoin([...promises, of(pictures), of(analysis)]);
  }

  private uploadPictures(value: [...TyreAnalysisResponseDto[], TyrePicture[], Analysis]) {
    const analysis = value.pop() as Analysis;
    const pictures = value.pop() as TyrePicture[];
    const responses = value as TyreAnalysisResponseDto[];
    pictures.forEach((picture, index) => {
      picture.uploadInfo = responses[index];
      this.photoshootService.setPicture(picture);
      this.uploadService.uploadPicture(picture);
    });
    return of(analysis);
  }
}
